import React, { useCallback, useEffect, useState } from 'react';
import { useProfile } from '../hooks/useProfile';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import AddInvestmentModal from '../components/AddInvestmentModal';
import MarketContext from '../components/MarketContext';
import {
  Investment, PortfolioSummary, calculatePortfolioYTD,
  formatReturn as formatPortfolioReturn, totalLabel,
} from '../lib/portfolioCalc';
import { calculateFirmYTD, FirmYTDResult } from '../lib/firmPerformance';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:      '#0F2554',
  navyDark:  '#091A3E',
  gold:      '#C9A84C',
  black:     '#0A0A0A',
  white:     '#FFFFFF',
  surface:   '#F5F6F8',
  surfaceAlt:'#ECEEF2',
  border:    '#D0D5DD',
  borderLt:  '#E8EBF0',
  text:      '#0A0A0A',
  textSec:   '#3D4552',
  muted:     '#6B7280',
  mutedLt:   '#9CA3AF',
  success:   '#0A7C42',
  danger:    '#C0222A',
  gold2:     '#A8873A',
};

interface ActivityItem {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string;
  created_at: string;
  read_at: string | null;
}

interface StrategyRow {
  asset_class: string;
  strategy: string;
  fund_count: number;
  last_month_return: number | null;
  ytd_return: number | null;
}

interface ChartDataPoint {
  label: string;
  [key: string]: number | string;
}

function timeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function activityDot(type: string): string {
  if (type === 'meeting_confirmed') return C.success;
  if (type === 'meeting_declined') return C.danger;
  if (type === 'connection_request') return '#1D6FA4';
  if (type === 'connection_approved') return C.success;
  if (type === 'performance_updated') return C.gold;
  return C.navy;
}

const assetClasses = [
  { value: 'all', label: 'All' },
  { value: 'hedge_fund', label: 'Hedge Funds' },
  { value: 'pe', label: 'Private Equity' },
  { value: 'vc', label: 'Venture Capital' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'credit', label: 'Credit' },
  { value: 'infrastructure', label: 'Infrastructure' },
];

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund',
  pe: 'Private Equity',
  vc: 'Venture Capital',
  real_estate: 'Real Estate',
  credit: 'Credit',
  infrastructure: 'Infrastructure',
};

const classColors: Record<string, string> = {
  hedge_fund:     '#0D6E8A',
  pe:             '#B07D2A',
  vc:             '#1D4ED8',
  real_estate:    '#B91C1C',
  credit:         '#0F766E',
  infrastructure: '#6D28D9',
};

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fullMonthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

async function fetchAllPerformance(fundIds: string[]) {
  const PAGE_SIZE = 1000;
  let allData: any[] = [];
  let page = 0;
  while (true) {
    const { data, error } = await supabase
      .from('fund_performance')
      .select('fund_id, year, month, return_pct')
      .in('fund_id', fundIds)
      .order('year', { ascending: true })
      .order('month', { ascending: true })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error || !data || data.length === 0) break;
    allData = [...allData, ...data];
    if (data.length < PAGE_SIZE) break;
    page++;
  }
  return allData;
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${C.border}`,
  borderRadius: '4px',
};

const sectionTitle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: C.muted,
  letterSpacing: '0.8px',
  textTransform: 'uppercase' as const,
  fontFamily: 'system-ui, sans-serif',
};

const monoNum: React.CSSProperties = {
  fontFamily: '"Courier New", Courier, monospace',
  fontWeight: 700,
};

// ── Firm YTD (manager) ────────────────────────────────────────────────────────
// Deliberately three lines only, so the card matches the height of the three
// stat cards beside it. Per-fund detail lives on Manage Funds.
function FirmYTDCard({ result, loading, onNavigate }: {
  result: FirmYTDResult | null;
  loading: boolean;
  onNavigate: (path: string) => void;
}) {
  const [hoverLink, setHoverLink] = useState(false);

  const ytd = result?.weightedYTD ?? null;
  const colour = ytd === null ? C.muted : ytd > 0 ? C.success : ytd < 0 ? C.danger : C.muted;
  const noFunds = !loading && (result?.fundCount ?? 0) === 0;
  const noData  = !loading && !noFunds && (result?.fundsWithData ?? 0) === 0;

  const linkStyle: React.CSSProperties = {
    fontSize: '11px', color: C.gold, cursor: 'pointer', fontFamily: 'system-ui',
    textDecoration: hoverLink ? 'underline' : 'none',
  };

  return (
    <div style={{ ...card, padding: '18px 20px', borderLeft: `3px solid ${colour}` }}>
      <style>{'@keyframes altvenoYtdPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.45 } }'}</style>

      <div style={{
        fontSize: '9px', color: C.gold, letterSpacing: '1.5px',
        textTransform: 'uppercase', fontFamily: 'system-ui, sans-serif',
        fontWeight: 700, marginBottom: '8px',
      }}>
        Firm YTD Return
      </div>

      <div style={{
        fontFamily: 'Georgia, serif', fontSize: '36px', lineHeight: 1,
        letterSpacing: '-0.5px', marginBottom: '6px',
        color: loading ? C.muted : colour,
        animation: loading ? 'altvenoYtdPulse 1.4s ease-in-out infinite' : undefined,
      }}>
        {loading ? '--' : formatPortfolioReturn(ytd)}
      </div>

      <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'system-ui' }}>
        {loading ? 'Calculating…'
          : noFunds ? 'No active funds'
          : result && result.fundsWithData > 0 ? `Weighted across ${result.coverageLabel}`
          : 'No performance data yet'}
      </div>

      {noFunds && (
        <div style={{ marginTop: '10px' }}>
          <span style={linkStyle}
            onMouseEnter={() => setHoverLink(true)} onMouseLeave={() => setHoverLink(false)}
            onClick={() => onNavigate('/manage-funds')}
          >Add your fund →</span>
        </div>
      )}

      {noData && (
        <div style={{ marginTop: '10px' }}>
          <span style={linkStyle}
            onMouseEnter={() => setHoverLink(true)} onMouseLeave={() => setHoverLink(false)}
            onClick={() => onNavigate('/manage-funds')}
          >Add Performance Data →</span>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { profile, firm, loading } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();

  // Routes parked behind a redirect (e.g. EventConnect) pass a notice through.
  const routeNotice = (location.state as { notice?: string } | null)?.notice || '';
  const [notice, setNotice] = useState(routeNotice);
  useEffect(() => { setNotice(routeNotice); }, [routeNotice]);

  const [activity, setActivity]                   = useState<ActivityItem[]>([]);
  const [connections, setConnections]             = useState(0);
  const [meetingsThisMonth, setMeetingsThisMonth] = useState(0);
  const [pipeline, setPipeline]                   = useState<any[]>([]);
  const [tableRows, setTableRows]                 = useState<StrategyRow[]>([]);
  const [chartData, setChartData]                 = useState<ChartDataPoint[]>([]);
  const [activeTableClass, setActiveTableClass]   = useState('all');
  const [enabledLines, setEnabledLines]           = useState<Set<string>>(new Set(Object.keys(classLabels)));
  const [perfLoading, setPerfLoading]             = useState(true);
  const [latestMonthLabel, setLatestMonthLabel]   = useState('Last Month');
  const [latestYear, setLatestYear]               = useState(2026);
  const [investments, setInvestments]             = useState<Investment[]>([]);
  const [portfolio, setPortfolio]                 = useState<PortfolioSummary | null>(null);
  const [addInvestOpen, setAddInvestOpen]         = useState(false);
  const [firmYTD, setFirmYTD]                     = useState<FirmYTDResult | null>(null);
  const [ytdLoading, setYtdLoading]               = useState(true);

  const isAllocator = profile?.role === 'allocator';
  const isManager   = profile?.role === 'manager';

  // The manager mirror of the allocator tracker: YTD across their own funds.
  useEffect(() => {
    if (!firm || !isManager) { setYtdLoading(false); return; }
    let cancelled = false;
    setYtdLoading(true);
    calculateFirmYTD(firm.id).then(result => {
      if (cancelled) return;
      setFirmYTD(result);
      setYtdLoading(false);
    });
    return () => { cancelled = true; };
  }, [firm, isManager]);

  // The tracker is private to the allocator: RLS scopes these rows to
  // allocator_profile_id = auth.uid(), so this only ever returns their own.
  const fetchPortfolio = useCallback(async () => {
    if (!user || profile?.role !== 'allocator') return;
    const { data } = await supabase.from('allocator_investments')
      .select('*').eq('is_active', true);
    const list = (data as Investment[]) || [];
    setInvestments(list);
    setPortfolio(await calculatePortfolioYTD(list));
  }, [user, profile?.role]);

  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);

  const firstName = profile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'there';
  const aum = firm?.aum_exact ? `$${firm.aum_exact}mm` :
    firm?.aum_min && firm?.aum_max ? `$${firm.aum_min}–${firm.aum_max}mm` : '-';

  useEffect(() => {
    if (!user || !firm) return;
    fetchActivity(); fetchStats(); fetchPipeline();
  }, [user, firm]);

  useEffect(() => { fetchPerformanceData(); }, []);

  const fetchActivity = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('notifications').select('*').eq('user_id', user.id)
      .order('created_at', { ascending: false }).limit(8);
    if (data) setActivity(data);
  };

  const fetchStats = async () => {
    if (!firm) return;
    const { count: connCount } = await supabase
      .from('connections').select('*', { count: 'exact', head: true })
      .eq('status', 'approved')
      .or(`allocator_firm_id.eq.${firm.id},manager_firm_id.eq.${firm.id}`);
    setConnections(connCount || 0);
    const startOfMonth = new Date();
    startOfMonth.setDate(1); startOfMonth.setHours(0, 0, 0, 0);
    const { data: connData } = await supabase
      .from('connections').select('id').eq('status', 'approved')
      .or(`allocator_firm_id.eq.${firm.id},manager_firm_id.eq.${firm.id}`);
    if (connData?.length) {
      const { count: meetCount } = await supabase
        .from('meetings').select('*', { count: 'exact', head: true })
        .in('connection_id', connData.map(c => c.id))
        .eq('status', 'confirmed')
        .gte('scheduled_at', startOfMonth.toISOString());
      setMeetingsThisMonth(meetCount || 0);
    }
  };

  const fetchPipeline = async () => {
    if (!firm) return;
    const { data } = await supabase
      .from('connections')
      .select(`id, status, pipeline_stage,
        allocator_firm:firms!connections_allocator_firm_id_fkey(id, name),
        manager_firm:firms!connections_manager_firm_id_fkey(id, name),
        fund:funds(id, name)`)
      .or(`allocator_firm_id.eq.${firm.id},manager_firm_id.eq.${firm.id}`)
      .eq('status', 'approved').limit(5);
    if (data) setPipeline(data);
  };

  const fetchPerformanceData = async () => {
    const { data: funds } = await supabase
      .from('funds').select('id, asset_class, strategy').eq('status', 'open');
    if (!funds) { setPerfLoading(false); return; }
    const perfData = await fetchAllPerformance(funds.map(f => f.id));
    if (!perfData.length) { setPerfLoading(false); return; }

    const perfByFund: Record<string, any[]> = {};
    perfData.forEach((p: any) => {
      if (!perfByFund[p.fund_id]) perfByFund[p.fund_id] = [];
      perfByFund[p.fund_id].push(p);
    });

    const latestYM = perfData.reduce((max: number, p: any) => Math.max(max, p.year * 100 + p.month), 0);
    const ly = Math.floor(latestYM / 100);
    const lm = latestYM % 100;
    setLatestYear(ly);
    setLatestMonthLabel(`${fullMonthNames[lm - 1]} ${ly}`);

    const byAssetClass: Record<string, string[]> = {};
    funds.forEach(f => {
      if (!byAssetClass[f.asset_class]) byAssetClass[f.asset_class] = [];
      byAssetClass[f.asset_class].push(f.id);
    });

    const byStrategy: Record<string, { asset_class: string; strategy: string; fund_ids: string[] }> = {};
    funds.forEach(f => {
      const key = `${f.asset_class}__${f.strategy}`;
      if (!byStrategy[key]) byStrategy[key] = { asset_class: f.asset_class, strategy: f.strategy, fund_ids: [] };
      byStrategy[key].fund_ids.push(f.id);
    });

    const rows: StrategyRow[] = [];
    for (const [, group] of Object.entries(byStrategy)) {
      const fundPerfs = group.fund_ids.map(id => perfByFund[id] || []);
      const lastMonthReturns = fundPerfs
        .map(perfs => perfs.find((p: any) => p.year === ly && p.month === lm)?.return_pct)
        .filter((v): v is number => v !== undefined);
      const ytdReturns = fundPerfs.map(perfs => {
        const yp = perfs.filter((p: any) => p.year === ly && p.month <= lm);
        if (!yp.length) return null;
        return (yp.reduce((acc: number, p: any) => acc * (1 + p.return_pct / 100), 1) - 1) * 100;
      }).filter((v): v is number => v !== null);
      rows.push({
        asset_class: group.asset_class,
        strategy: group.strategy,
        fund_count: group.fund_ids.length,
        last_month_return: lastMonthReturns.length ? lastMonthReturns.reduce((a, b) => a + b, 0) / lastMonthReturns.length : null,
        ytd_return: ytdReturns.length ? ytdReturns.reduce((a, b) => a + b, 0) / ytdReturns.length : null,
      });
    }
    rows.sort((a, b) => a.asset_class.localeCompare(b.asset_class) || a.strategy.localeCompare(b.strategy));
    setTableRows(rows);

    const allMonths = [...new Set(perfData.map((p: any) => `${p.year}-${String(p.month).padStart(2, '0')}`))].sort() as string[];
    const runningIndex: Record<string, number> = {};
    Object.keys(byAssetClass).forEach(ac => { runningIndex[ac] = 100; });
    const cumulativeChart: ChartDataPoint[] = allMonths.map(ym => {
      const [year, month] = ym.split('-').map(Number);
      const point: ChartDataPoint = { label: `${monthNames[month - 1]} ${year}` };
      Object.entries(byAssetClass).forEach(([ac, fundIds]) => {
        const returns = fundIds
          .map(id => perfByFund[id]?.find((p: any) => p.year === year && p.month === month)?.return_pct)
          .filter((v): v is number => v !== undefined);
        if (returns.length) {
          runningIndex[ac] = runningIndex[ac] * (1 + returns.reduce((a, b) => a + b, 0) / returns.length / 100);
          point[ac] = parseFloat(runningIndex[ac].toFixed(2));
        }
      });
      return point;
    });
    setChartData(cumulativeChart);
    setPerfLoading(false);
  };

  const toggleLine = (ac: string) => {
    setEnabledLines(prev => {
      const next = new Set(prev);
      next.has(ac) ? next.delete(ac) : next.add(ac);
      return next;
    });
  };

  const stageConfig: Record<string, { label: string; color: string }> = {
    intro_meeting:      { label: 'Intro Meeting',  color: '#1D6FA4' },
    idd:                { label: 'IDD',            color: C.gold2 },
    odd:                { label: 'ODD',            color: '#5B21B6' },
    under_consideration:{ label: 'Under Review',   color: C.muted },
    invested:           { label: 'Invested',       color: C.success },
    passed:             { label: 'Passed',         color: C.danger },
  };

  // The fourth card is the allocator's portfolio widget, rendered separately
  // because it carries its own actions.
  const stats = [
    { label: 'Total AUM',           value: aum,                       sub: 'As reported',      accent: C.navy },
    { label: 'Active Connections',  value: String(connections),        sub: 'Approved',         accent: '#1D6FA4' },
    { label: 'Meetings This Month', value: String(meetingsThisMonth),  sub: 'Confirmed',        accent: C.gold },
  ];

  const portfolioYtdColour = portfolio?.ytdReturn == null ? C.muted
    : portfolio.ytdReturn > 0 ? C.success : portfolio.ytdReturn < 0 ? C.danger : C.navy;

  const portfolioManagers = new Set(
    investments.map(i => i.manager_firm_id || i.manager_name),
  ).size;

  const filteredRows = activeTableClass === 'all'
    ? tableRows
    : tableRows.filter(r => r.asset_class === activeTableClass);

  const returnColor = (v: number | null) =>
    v === null ? C.muted : v > 0 ? C.success : C.danger;

  const formatReturn = (v: number | null) =>
    v === null ? '-' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;

  const getGreeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        background: C.white, border: `1px solid ${C.border}`,
        borderRadius: '3px', padding: '10px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        fontSize: '12px', fontFamily: 'system-ui',
      }}>
        <div style={{ color: C.muted, marginBottom: '8px', fontWeight: 600, fontSize: '11px', letterSpacing: '0.3px' }}>{label}</div>
        {payload.map((p: any) => (
          <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '4px' }}>
            <span style={{ color: C.textSec }}>{classLabels[p.dataKey]}</span>
            <span style={{ ...monoNum, color: C.text, fontSize: '12px' }}>{p.value?.toFixed(1)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading...</div>
    </div>
  );

  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Redirect notice ── */}
      {notice && (
        <div style={{
          ...card, borderLeft: `3px solid ${C.gold}`,
          padding: '10px 14px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '12px',
          fontSize: '12.5px', color: C.textSec,
        }}>
          <span style={{ flex: 1 }}>{notice}</span>
          <button onClick={() => setNotice('')} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: C.mutedLt, fontSize: '15px', lineHeight: 1, padding: '0 2px',
          }} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', paddingBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
        <div>
          <div style={{ fontSize: '11px', color: C.muted, fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '6px' }}>
            {today}
          </div>
          <h1 style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: '24px', color: C.black,
            fontWeight: 400, margin: 0, lineHeight: 1.3,
          }}>
            {getGreeting()}, {firstName}
          </h1>
          <p style={{ fontSize: '13px', color: C.muted, margin: '4px 0 0' }}>
            {firm ? `${firm.name} · ${firm.headquarters}` : 'Welcome to Altveno'}
          </p>
        </div>
        <div style={{
          fontSize: '11px', color: C.muted,
          textAlign: 'right', lineHeight: 1.8,
        }}>
          <div style={{ fontWeight: 600, color: C.textSec }}>
            {profile?.role === 'manager' ? 'Fund Manager' : 'Allocator'}
          </div>
          <div>{firm?.name}</div>
        </div>
      </div>

      {/* ── Market context ── */}
      <MarketContext />

      {/* ── Stat cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {stats.map((stat, i) => (
          <div key={i} style={{
            ...card,
            padding: '16px 18px',
            borderLeft: `3px solid ${stat.accent}`,
          }}>
            <div style={{ ...sectionTitle, marginBottom: '8px', color: C.muted }}>
              {stat.label}
            </div>
            <div style={{
              fontFamily: 'Georgia, serif',
              fontSize: '28px', color: C.black,
              lineHeight: 1, marginBottom: '6px',
              letterSpacing: '-0.5px',
            }}>
              {stat.value}
            </div>
            <div style={{ fontSize: '11px', color: C.mutedLt }}>
              {stat.sub}
            </div>
          </div>
        ))}

        {/* ── Portfolio YTD: allocators get the live tracker, everyone else
             keeps the placeholder card so the grid stays four across. ── */}
        {isAllocator ? (
          <div style={{ ...card, padding: '16px 18px', borderLeft: `3px solid ${portfolioYtdColour}` }}>
            <div style={{ ...sectionTitle, marginBottom: '8px', color: C.muted }}>Portfolio YTD</div>
            <div style={{
              fontFamily: 'Georgia, serif', fontSize: '28px', color: portfolioYtdColour,
              lineHeight: 1, marginBottom: '6px', letterSpacing: '-0.5px',
            }}>
              {formatPortfolioReturn(portfolio?.ytdReturn ?? null)}
            </div>
            <div style={{ fontSize: '11px', color: C.mutedLt, marginBottom: '10px' }}>
              {investments.length === 0
                ? 'No investments tracked'
                : `Total invested: ${totalLabel(portfolio?.totalInvested || 0, portfolio?.currencies || [])} across ${portfolioManagers} manager${portfolioManagers !== 1 ? 's' : ''}`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => setAddInvestOpen(true)} style={{
                background: C.gold, color: C.navy, border: 'none', borderRadius: '3px',
                padding: '6px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui',
              }}>Add Investment</button>
              <span onClick={() => navigate('/portfolio')} style={{
                fontSize: '11px', color: C.navy, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.2px',
              }}>View Portfolio →</span>
            </div>
          </div>
        ) : isManager ? (
          <FirmYTDCard result={firmYTD} loading={ytdLoading} onNavigate={navigate} />
        ) : (
          <div style={{ ...card, padding: '16px 18px', borderLeft: `3px solid ${C.muted}` }}>
            <div style={{ ...sectionTitle, marginBottom: '8px', color: C.muted }}>Portfolio YTD</div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '28px', color: C.black, lineHeight: 1, marginBottom: '6px', letterSpacing: '-0.5px' }}>-</div>
            <div style={{ fontSize: '11px', color: C.mutedLt }}>Allocator feature</div>
          </div>
        )}
      </div>

      {addInvestOpen && user && (
        <AddInvestmentModal
          allocatorProfileId={user.id}
          allocatorFirmId={firm?.id || null}
          onClose={() => setAddInvestOpen(false)}
          onSaved={() => { setAddInvestOpen(false); fetchPortfolio(); }}
        />
      )}

      {/* ── Pipeline + Activity ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '16px', marginBottom: '16px' }}>

        {/* Pipeline */}
        <div style={card}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>
                {profile?.role === 'allocator' ? 'Manager Pipeline' : 'Investor Pipeline'}
              </div>
              <div style={{ fontSize: '11px', color: C.muted, marginTop: '1px' }}>Active connection stages</div>
            </div>
            <span onClick={() => navigate('/pipeline')} style={{ fontSize: '11px', color: C.navy, cursor: 'pointer', fontWeight: 600, letterSpacing: '0.2px' }}>
              VIEW ALL →
            </span>
          </div>
          {pipeline.length === 0 ? (
            <div style={{ padding: '36px', textAlign: 'center', fontSize: '13px', color: C.muted }}>
              No active connections yet
            </div>
          ) : pipeline.map((item: any, i) => {
            const af = Array.isArray(item.allocator_firm) ? item.allocator_firm[0] : item.allocator_firm;
            const mf = Array.isArray(item.manager_firm) ? item.manager_firm[0] : item.manager_firm;
            const fund = Array.isArray(item.fund) ? item.fund[0] : item.fund;
            const otherFirm = af?.id === firm?.id ? mf : af;
            const stage = item.pipeline_stage || 'intro_meeting';
            const sc = stageConfig[stage] || { label: stage, color: C.muted };
            return (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 18px',
                borderBottom: i < pipeline.length - 1 ? `1px solid ${C.borderLt}` : 'none',
                cursor: 'pointer',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = C.surface)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{
                  width: '32px', height: '32px', borderRadius: '3px',
                  background: C.navy,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700, color: C.gold, flexShrink: 0,
                  fontFamily: 'Georgia, serif',
                }}>
                  {otherFirm?.name?.charAt(0) || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>{otherFirm?.name}</div>
                  <div style={{ fontSize: '11px', color: C.muted, marginTop: '1px' }}>{fund?.name}</div>
                </div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: sc.color, letterSpacing: '0.5px', textTransform: 'uppercase', flexShrink: 0 }}>
                  {sc.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Activity */}
        <div style={card}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>Recent Activity</div>
          </div>
          <div>
            {activity.length === 0 ? (
              <div style={{ padding: '36px', textAlign: 'center', fontSize: '13px', color: C.muted }}>
                No activity yet
              </div>
            ) : activity.map((item, i) => (
              <div key={item.id}
                onClick={() => item.link && navigate(item.link)}
                style={{
                  display: 'flex', gap: '10px', padding: '10px 16px',
                  borderBottom: i < activity.length - 1 ? `1px solid ${C.borderLt}` : 'none',
                  cursor: item.link ? 'pointer' : 'default',
                  opacity: item.read_at ? 0.5 : 1,
                }}
                onMouseEnter={e => { if (item.link) (e.currentTarget as HTMLDivElement).style.background = C.surface; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                <div style={{
                  width: '5px', height: '5px', borderRadius: '50%',
                  background: activityDot(item.type),
                  marginTop: '6px', flexShrink: 0,
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: C.black, lineHeight: 1.5 }}>{item.body}</div>
                  <div style={{ fontSize: '10px', color: C.mutedLt, marginTop: '2px', ...monoNum }}>{timeAgo(item.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Chart + Table ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

        {/* Cumulative performance */}
        <div style={{ ...card, padding: '18px' }}>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>Cumulative Performance</div>
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
              Indexed to 100 · Anonymous aggregated returns by asset class
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '14px' }}>
            {Object.entries(classLabels).map(([ac, label]) => {
              const on = enabledLines.has(ac);
              return (
                <button key={ac} onClick={() => toggleLine(ac)} style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '2px 8px', borderRadius: '2px', fontSize: '10px',
                  cursor: 'pointer', fontFamily: 'system-ui', fontWeight: on ? 600 : 400,
                  border: `1px solid ${on ? classColors[ac] : C.border}`,
                  background: on ? classColors[ac] + '10' : C.white,
                  color: on ? classColors[ac] : C.muted,
                  transition: 'all 0.1s',
                  letterSpacing: '0.2px',
                }}>
                  <div style={{ width: '10px', height: '2px', background: on ? classColors[ac] : C.border }} />
                  {label}
                </button>
              );
            })}
          </div>

          {perfLoading ? (
            <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: '13px' }}>
              Loading...
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mutedLt, fontFamily: 'system-ui' }} tickLine={false} axisLine={{ stroke: C.border }} interval={5} />
                <YAxis tick={{ fontSize: 10, fill: C.mutedLt, fontFamily: 'system-ui' }} tickLine={false} axisLine={false} tickFormatter={v => `${v.toFixed(0)}`} />
                <Tooltip content={<CustomTooltip />} />
                {Object.keys(classLabels).map(ac => (
                  enabledLines.has(ac) && (
                    <Line key={ac} type="monotone" dataKey={ac}
                      stroke={classColors[ac]} strokeWidth={1.5}
                      dot={false} connectNulls />
                  )
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}

          <div style={{ fontSize: '10px', color: C.mutedLt, marginTop: '10px', borderTop: `1px solid ${C.borderLt}`, paddingTop: '8px', letterSpacing: '0.2px' }}>
            All returns anonymous and aggregated · Updated monthly
          </div>

          {/* These lines are the market aggregate, not the manager's own book.
              A manager whose funds have gaps is told where their own YTD comes
              from, since this chart will never show it. */}
          {isManager && firmYTD && firmYTD.fundsWithData < firmYTD.fundCount && (
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '8px', lineHeight: 1.6 }}>
              Add monthly returns in{' '}
              <span onClick={() => navigate('/manage-funds')} style={{ color: C.gold, cursor: 'pointer', fontWeight: 600 }}>
                Manage Funds
              </span>{' '}
              to see your YTD performance here.
            </div>
          )}
        </div>

        {/* Market performance table */}
        <div style={{ ...card, padding: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>Market Performance</div>
              <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                {latestMonthLabel} + YTD · Strategy level · Anonymous
              </div>
            </div>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {assetClasses.map(ac => (
              <button key={ac.value} onClick={() => setActiveTableClass(ac.value)} style={{
                padding: '3px 8px', borderRadius: '2px', fontSize: '10px',
                cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500,
                border: `1px solid ${activeTableClass === ac.value ? C.navy : C.border}`,
                background: activeTableClass === ac.value ? C.navy : C.white,
                color: activeTableClass === ac.value ? C.white : C.muted,
                transition: 'all 0.1s',
                letterSpacing: '0.2px',
              }}>{ac.label}</button>
            ))}
          </div>

          {perfLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: C.muted, fontSize: '13px' }}>Loading...</div>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: '300px', border: `1px solid ${C.border}` }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: C.navy }}>
                    <th style={{ textAlign: 'left', padding: '7px 10px', fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: '0.5px' }}>STRATEGY</th>
                    <th style={{ textAlign: 'right', padding: '7px 10px', fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: '0.5px' }}>{latestMonthLabel.toUpperCase()}</th>
                    <th style={{ textAlign: 'right', padding: '7px 10px', fontSize: '10px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: '0.5px' }}>YTD {latestYear}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, i) => (
                    <tr key={i}
                      style={{ borderBottom: `1px solid ${C.borderLt}`, background: i % 2 === 0 ? C.white : C.surface }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#EBF0FA')}
                      onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? C.white : C.surface)}
                    >
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontSize: '12px', color: C.black, fontWeight: 500 }}>{row.strategy}</div>
                        <div style={{ fontSize: '10px', color: classColors[row.asset_class] || C.muted, marginTop: '1px', fontWeight: 600 }}>
                          {classLabels[row.asset_class]} · {row.fund_count} funds
                        </div>
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', ...monoNum, fontSize: '12px', color: returnColor(row.last_month_return) }}>
                        {formatReturn(row.last_month_return)}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', ...monoNum, fontSize: '12px', color: returnColor(row.ytd_return) }}>
                        {formatReturn(row.ytd_return)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredRows.length === 0 && (
                <div style={{ textAlign: 'center', padding: '36px', color: C.muted, fontSize: '13px' }}>
                  No data for this asset class
                </div>
              )}
            </div>
          )}
          <div style={{ fontSize: '10px', color: C.mutedLt, marginTop: '10px', borderTop: `1px solid ${C.borderLt}`, paddingTop: '8px', letterSpacing: '0.2px' }}>
            All returns anonymous and aggregated · Updated monthly
          </div>
        </div>
      </div>
    </div>
  );
}